import React from 'react';
import { SeoHead } from '../components/SeoHead';
import { getStaticPageMetadata } from '../lib/seo';
import { PageContainer } from '../src/components/ui/page-container';
import StateDirectorySwitch from '../components/directory/StateDirectorySwitch';

/**
 * '/gyms' — the STATE CHOOSER (UI-57 flip, operator-ruled 2026-08-14, ruling v:
 * PURE chooser — the search box and map moved to the per-state directories).
 *
 * The page does exactly one job: ask which state, then travel. Its heading is the
 * existing /gyms title (derived from STATIC_PAGE_META, never retyped — the string
 * finally becomes TRUE here, since the page now genuinely offers both states); the
 * prompt line is the operator's verbatim wording (ruling vi); the pills are the
 * shared StateDirectorySwitch in chooser mode (currentState null → both travel),
 * which CARRIES inbound ?styles/?age/?exp through to the chosen state — that is
 * what keeps the five blog style-CTAs landing here alive with one added click
 * (ruled; item-16's baking half needs no change).
 *
 * Scaffold follows the static-page convention (AboutPage): SeoHead from
 * STATIC_PAGE_META, the h-16 header spacer, one main landmark with
 * id="main-content" (safe here: the DirectoryPage overlay no longer treats /gyms
 * as directory space post-flip, so its conditional id never competes on this page).
 * Plain markup throughout — conditionally-rendered branches do not survive
 * prerendering; everything here must.
 */
const StateChooserPage: React.FC = () => {
    const meta = getStaticPageMetadata('/gyms')!;
    // Operator-ruled 2026-08-14: the VISIBLE heading drops the ' – Martial Arts
    // Network' suffix (it belongs to tabs and SERPs; every other page's h1 does the
    // same) while staying DERIVED from the one metadata source — a future title
    // ruling still propagates. The tab title/SERP form is untouched.
    const displayHeading = meta.title.split(' – ')[0]!;
    return (
        <div className="min-h-screen bg-surface-canvas flex flex-col">
            <SeoHead meta={meta} />
            <div className="h-header flex-shrink-0" />
            <main id="main-content" className="flex-grow text-text-primary">
                <PageContainer size="narrow" className="py-16">
                    {/* ⭐ CENTRED (operator-ruled 2026-08-16). `text-center` on the heading and
                        the prompt only — NOT on the container — so the pill bar below keeps its
                        own full-width layout instead of inheriting text centring it does not want. */}
                    <h1 className="text-3xl font-semibold text-brand-primary mb-4 text-center">{displayHeading}</h1>
                    {/* ⚠️ THE SECOND SENTENCE IS A FORWARD-LOOKING PUBLIC CLAIM on an indexed
                        page (operator-ruled 2026-08-16, their wording). Flagged at the gate and
                        ruled anyway. If the roadmap changes, this line is a promise that has to
                        change with it — it is not decoration. */}
                    <p className="text-text-muted text-base mb-8 text-center">Which state are you searching in? More states coming soon!</p>
                    <StateDirectorySwitch currentState={null} />
                </PageContainer>
            </main>
        </div>
    );
};

export default StateChooserPage;
